import React from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    ReferenceInput,
    AutocompleteInput,
    required,
    minValue,
    maxValue,
    maxLength,
    useTranslate,
    useRedirect,
    useNotify,
    Toolbar,
    SaveButton,
    FormDataConsumer,
} from 'react-admin';
import { 
    Box, 
    Card, 
    CardContent, 
    Typography,
    IconButton,
    Alert,
} from '@mui/material';
import {
    ArrowBack,
    Power,
    LocationOn,
    ElectricBolt,
} from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const KNOWN_LOAD_GROUP_FIELDS = new Set([
    'name', 'siteId', 'description', 'location',
    'maxPower', 'voltage', 'maxCurrent', 'phase',
    'priorityLevel', 'loadBalancingEnabled', 'status',
    'lmsTemplateId', 'ocppServerId',
]);

const mapLoadGroupBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const loadGroupFieldLabelKey = (field: string): string =>
    KNOWN_LOAD_GROUP_FIELDS.has(field)
        ? `resources.load-groups.fields.${field}`
        : field;

const CreateToolbar = () => {
    return (
        <Toolbar 
            sx={{ 
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton 
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/load-groups')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.load-groups.create.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.load-groups.create.title')}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }} color="text.secondary">
                    {translate('resources.load-groups.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};


export const LoadGroupCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    const transform = (data: any) => {
        // Clean up the data before sending to the backend
        const cleanedData = { ...data };
        
        // Convert string numbers to actual numbers
        if (cleanedData.voltage !== undefined) {
            cleanedData.voltage = Number(cleanedData.voltage);
        }
        if (cleanedData.maxCurrent !== undefined) {
            cleanedData.maxCurrent = Number(cleanedData.maxCurrent);
        }
        if (cleanedData.maxPower !== undefined) {
            cleanedData.maxPower = Number(cleanedData.maxPower);
        }
        if (cleanedData.priorityLevel !== undefined) {
            cleanedData.priorityLevel = Number(cleanedData.priorityLevel);
        }
        
        // Convert loadBalancingEnabled from number to boolean if needed
        if (typeof cleanedData.loadBalancingEnabled === 'number') {
            cleanedData.loadBalancingEnabled = cleanedData.loadBalancingEnabled === 1;
        }
        
        // Convert null values to undefined for optional string fields
        if (cleanedData.description === null || cleanedData.description === '') {
            delete cleanedData.description;
        }
        if (cleanedData.location === null || cleanedData.location === '') {
            delete cleanedData.location;
        }
        if (cleanedData.lmsTemplateId === null || cleanedData.lmsTemplateId === '') {
            delete cleanedData.lmsTemplateId;
        }
        if (cleanedData.ocppServerId === null || cleanedData.ocppServerId === '') {
            delete cleanedData.ocppServerId;
        }
        
        return cleanedData;
    };
    
    return (
        <Create
            title={false}
            component="div"
            redirect="list"
            transform={transform}
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.load-groups.notifications.created', { type: 'success' });
                    redirect('/load-groups');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapLoadGroupBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(loadGroupFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />
                    {/* Basic Information and Site Assignment */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <LocationOn sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.load-groups.sections.basic')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="name"
                                            validate={[
                                                required(),
                                                maxLength(100, translate("resources.load-groups.validation.nameTooLong")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.name')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <ReferenceInput source="siteId" reference="sites">
                                            <SelectInput
                                                optionText="name"
                                                fullWidth
                                                helperText={translate('resources.load-groups.helpers.site')}
                                                validate={[required()]}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </ReferenceInput>
                                    </Box>
                                </Box>
                                
                                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <TextInput
                                        source="location"
                                        validate={[
                                            maxLength(200, translate("resources.load-groups.validation.locationTooLong")),
                                        ]}
                                        fullWidth
                                        helperText={translate('resources.load-groups.helpers.location')}
                                        sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                    />
                                </Box>
                                
                                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <TextInput
                                        source="description"
                                        multiline
                                        rows={3}
                                        validate={[
                                            maxLength(500, translate("resources.load-groups.validation.descriptionTooLong")),
                                        ]}
                                        fullWidth
                                        helperText={translate('resources.load-groups.helpers.description')}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Electrical Configuration */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <ElectricBolt sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.load-groups.sections.electrical')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="phase"
                                            choices={[
                                                { id: 'single', name: translate('resources.load-groups.phases.single') },
                                                { id: 'three', name: translate('resources.load-groups.phases.three') },
                                            ]}
                                            validate={[required()]}
                                            defaultValue="three"
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.phase')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="voltage"
                                            validate={[
                                                required(),
                                                minValue(100, translate("resources.load-groups.validation.voltageTooLow")),
                                                maxValue(1000, translate("resources.load-groups.validation.voltageTooHigh")),
                                            ]}
                                            defaultValue={480}
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.voltage')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="maxCurrent"
                                            validate={[
                                                required(),
                                                minValue(0, translate("resources.load-groups.validation.currentNegative")),
                                                maxValue(5000, translate("resources.load-groups.validation.currentTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.maxCurrent')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="maxPower"
                                            validate={[
                                                required(),
                                                minValue(0, translate("resources.load-groups.validation.powerNegative")),
                                                maxValue(2000, translate("resources.load-groups.validation.powerTooHigh")),
                                            ]}
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.maxPower')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                
                                <FormDataConsumer>
                                    {({ formData }) => {
                                        if (formData.phase === 'single' && formData.voltage > 480) {
                                            return (
                                                <Alert severity="error">
                                                    {translate('resources.load-groups.validation.singlePhaseVoltage')}
                                                </Alert>
                                            );
                                        }
                                        if (formData.voltage && formData.maxCurrent) {
                                            const calculatedPower = formData.phase === 'three' 
                                                ? (formData.voltage * formData.maxCurrent * Math.sqrt(3)) / 1000
                                                : (formData.voltage * formData.maxCurrent) / 1000;
                                            return (
                                                <Alert severity="info">
                                                    {translate('resources.load-groups.info.calculatedPower', { power: calculatedPower.toFixed(1) })}
                                                </Alert>
                                            );
                                        }
                                        return null;
                                    }}
                                </FormDataConsumer>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Power Management Settings */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Power sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.load-groups.sections.powerManagement')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="priorityLevel"
                                            validate={[
                                                required(),
                                                minValue(1, translate("resources.load-groups.validation.priorityTooLow")),
                                                maxValue(10, translate("resources.load-groups.validation.priorityTooHigh")),
                                            ]}
                                            defaultValue={1}
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.priorityLevel')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="status"
                                            choices={[
                                                { id: 'active', name: translate('resources.load-groups.statuses.active') },
                                                { id: 'inactive', name: translate('resources.load-groups.statuses.inactive') },
                                                { id: 'maintenance', name: translate('resources.load-groups.statuses.maintenance') },
                                            ]}
                                            validate={[required()]}
                                            defaultValue="active"
                                            fullWidth
                                            helperText={translate('resources.load-groups.helpers.status')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                
                                <BooleanInput
                                    source="loadBalancingEnabled"
                                    defaultValue={true}
                                    sx={{ mb: 2 }}
                                />
                                
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <ReferenceInput
                                            source="ocppServerId"
                                            reference="backend/ocpp-servers"
                                            filter={{ status: 'active' }}
                                            sort={{ field: 'name', order: 'ASC' }}
                                            perPage={1000}
                                        >
                                            <SelectInput
                                                optionText="name"
                                                fullWidth
                                                emptyText="None"
                                                helperText={translate('resources.load-groups.helpers.ocppServer')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </ReferenceInput>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <ReferenceInput
                                            source="lmsTemplateId"
                                            reference="backend/lms-templates"
                                            filter={{ all: 'true' }}
                                            sort={{ field: 'name', order: 'ASC' }}
                                            perPage={1000}
                                            filterToQuery={(searchText: string) => ({ search: searchText, all: 'true' })}
                                        >
                                            <AutocompleteInput
                                                optionText="name"
                                                fullWidth
                                                emptyText="None"
                                                helperText={translate('resources.load-groups.helpers.lmsTemplate')}
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                                filterToQuery={(searchText: string) => searchText}
                                                shouldRenderSuggestions={(val: string) => val.trim().length >= 0}
                                            />
                                        </ReferenceInput>
                                    </Box>
                                </Box>
                                
                                <Alert severity="info">
                                    {translate('resources.load-groups.info.priorityNote')}
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};
